import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Badge,
  BadgeOwnProps,
  FormControl,
  FormControlProps,
  FormHelperText,
  MenuItem,
  SelectChangeEvent,
  SelectProps,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material";
import MuiSelect from "@mui/material/Select";
import { LabelLink } from "components/FieldLabel/types";
import React, { ReactNode } from "react";
import { FieldError } from "react-hook-form";
import FieldLabel from "../FieldLabel";
import getInputVariantSx from "./inputStyles";

export interface ISelectItem {
  value: string;
  label: string;
  badgeColor?: BadgeOwnProps["color"];
}

const renderPlaceholder = (value: ReactNode) => <Typography color='text.disabled'>{value}</Typography>;

export type SelectCustomProps<TSelectItem extends object = ISelectItem> = {
  items: TSelectItem[];
  fullWidth?: boolean;
  labelName?: keyof TSelectItem;
  valueName?: keyof TSelectItem;
  labelLink?: LabelLink;
  labelProps?: TypographyProps;
  renderValue?: (value: TSelectItem) => React.ReactNode;
  renderItem?: (item: TSelectItem) => React.ReactNode;
  onChange?: (event: SelectChangeEvent<TSelectItem>, child: React.ReactNode) => void;
  textColor?: "primary";
  onItemClick?: (item: TSelectItem) => void;
  error?: FieldError;
  value?: TSelectItem[keyof TSelectItem];
  required?: boolean;
  formControlProps?: FormControlProps;
  hint?: string;
  endAction?: ReactNode;
} & Omit<SelectProps, "onChange" | "error" | "renderValue" | "value">;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Select = <TSelectItem extends Record<string, any> = ISelectItem>({
  items,
  value,
  onChange,
  sx,
  labelName = "label",
  labelProps,
  valueName = "value",
  readOnly,
  label,
  labelLink,
  renderValue,
  renderItem,
  onItemClick,
  error,
  hint,
  variant,
  placeholder,
  required,
  disabled,
  formControlProps,
  endAction,
  ...props
}: SelectCustomProps<TSelectItem>) => {
  const handleChange = (event: SelectChangeEvent<unknown>, child: ReactNode) => {
    const typedEvent = event as SelectChangeEvent<TSelectItem>;
    onChange?.(typedEvent, child);
  };
  const isCurrentItemWithBadge = Boolean(
    items.find((item) => (item[valueName || "value"] as string) === value)?.badgeColor as BadgeOwnProps["color"]
  );
  const selectedItem = items.find((item) => item[valueName || "value"] === value);

  const getRenderValue = () => {
    if (!value && (placeholder || label)) {
      return renderPlaceholder.bind(null, placeholder || label);
    }
    if (renderValue && selectedItem) return renderValue.bind(null, selectedItem);
    return undefined;
  };

  const renderMenuItem = (item: TSelectItem, itemLabel: ReactNode, withBadge: BadgeOwnProps["color"] | undefined) => {
    if (renderItem) return renderItem(item);
    if (!withBadge) return itemLabel;

    return (
      <Badge
        color={item.badgeColor}
        anchorOrigin={{ horizontal: "left", vertical: "top" }}
        variant='dot'
        slotProps={{
          badge: {
            style: {
              top: "50%",
              left: -12,
              minWidth: 12,
              height: 12,
              borderRadius: "100%",
            },
          },
        }}>
        {itemLabel}
      </Badge>
    );
  };

  return (
    <FormControl
      fullWidth={props.fullWidth}
      component={Stack}
      spacing={0.5}
      error={!!error}
      {...formControlProps}>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          labelLink={labelLink}
          disabled={disabled}
          typographyProps={labelProps}
        />
      )}
      <Stack
        spacing={1.5}
        useFlexGap
        direction='row'>
        <MuiSelect
          value={value || null}
          size='small'
          IconComponent={KeyboardArrowDown}
          onChange={handleChange}
          sx={getInputVariantSx(variant, sx, { width: "100%" })}
          variant={variant}
          displayEmpty
          disabled={disabled}
          renderValue={getRenderValue()}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
            transformOrigin: {
              vertical: 0,
              horizontal: "center",
            },
          }}
          inputProps={
            isCurrentItemWithBadge
              ? {
                  sx: { paddingLeft: 4 },
                }
              : {}
          }
          {...props}>
          {items.map((item) => {
            const itemLabel = item[labelName || "label"];
            const itemValue = item[valueName || "value"];
            const withBadge = item.badgeColor;

            return (
              <MenuItem
                key={`select-item-${itemValue}`}
                onClick={() => onItemClick?.(item)}
                textColor={props.textColor}
                value={itemValue}
                sx={withBadge ? { paddingLeft: 4 } : {}}>
                {renderMenuItem(item, itemLabel, withBadge)}
              </MenuItem>
            );
          })}
        </MuiSelect>
        {endAction}
      </Stack>
      {(error?.message || hint) && <FormHelperText>{error?.message || hint}</FormHelperText>}
    </FormControl>
  );
};

export default Select;
